import type { BaseQueryApi } from "@reduxjs/toolkit/query";
import { fetchBaseQuery } from "@reduxjs/toolkit/query";
import type { Controller } from "./controller";
import type { HomeHandle, HttpObservation } from "./contracts";
import { safely } from "./contracts";
import { apiRegistry } from "./registry";

export interface RequestDiagnostic {
  phase: "base_query" | "physical_start" | "physical_end";
  name: string;
  home?: HomeHandle;
  captured?: boolean;
  status?: number;
  reason?: "no_capture" | "closed_or_disabled" | "origin" | "execution";
}

type Query = ReturnType<typeof fetchBaseQuery>;
type FetchBaseQueryArgs = NonNullable<Parameters<typeof fetchBaseQuery>[0]>;
type Capture = {
  home: HomeHandle | undefined;
  token: NonNullable<ReturnType<Controller["captureRequest"]>>;
  name: string;
};
/** RTK creates a BaseQueryApi per execution. No carrier in args, cache keys or state. */
export function createRequestAdapter(
  controller: Controller,
  diagnostic?: (event: RequestDiagnostic) => void
) {
  const owners = new WeakMap<BaseQueryApi, Capture>();
  function withTelemetry<
    T extends (args: any, api: BaseQueryApi, extraOptions: any) => unknown
  >(slice: string, query: T): T {
    const wrapped = (
      args: Parameters<T>[0],
      api: BaseQueryApi,
      extraOptions: Parameters<T>[2]
    ) => {
      let homeAtEntry: HomeHandle | undefined;
      const capture = safely(() => {
        const home = controller.captureHome();
        homeAtEntry = home;
        const name = apiRegistry.lookup(slice, api.endpoint);
        const token = name ? controller.captureRequest(name) : undefined;
        return token && name && !owners.has(api)
          ? { home, token, name }
          : undefined;
      }, undefined);
      safely(
        () =>
          diagnostic?.({
            phase: "base_query",
            name: apiRegistry.lookup(slice, api.endpoint) ?? "",
            home: homeAtEntry,
            captured: !!capture,
            reason: capture ? "execution" : "no_capture",
          }),
        undefined
      );
      if (!capture) return query(args, api, extraOptions);
      owners.set(api, capture); // Synchronous D20 capture, before business/auth awaits.
      const cleanup = () => {
        if (owners.get(api) === capture) owners.delete(api);
      };
      try {
        return Promise.resolve(query(args, api, extraOptions)).finally(cleanup);
      } catch (error) {
        cleanup();
        throw error;
      }
    };
    // Preserve the business function's inferred result/error types in RTK hooks.
    return wrapped as T;
  }
  function physicalBaseQuery(options: FetchBaseQueryArgs): Query {
    const raw = fetchBaseQuery(options);
    return (args, api, extraOptions) => {
      const captured = safely(() => owners.get(api), undefined);
      if (!captured) return raw(args, api, extraOptions);
      let observation: HttpObservation | undefined;
      const fetchFn: NonNullable<FetchBaseQueryArgs["fetchFn"]> = (
        input,
        init
      ) => {
        // fetchBaseQuery invokes this after prepareHeaders/auth. No await between
        // the final gate and fetch; a killed generation cannot add new headers.
        const outgoing = safely(() => {
          if (!(input instanceof Request)) return input;
          observation = controller.startRequest(captured.token, {
            origin: new URL(input.url).origin,
            method: input.method,
            url: input.url,
          });
          safely(
            () =>
              diagnostic?.({
                phase: "physical_start",
                name: captured.name,
                home: captured.home,
                captured: !!observation,
                reason: observation ? "execution" : "closed_or_disabled",
              }),
            undefined
          );
          return controller.attachRequestHeaders(input, observation);
        }, input);
        return (options.fetchFn ?? fetch)(outgoing, init);
      };
      const execute = safely(
        () => fetchBaseQuery({ ...options, fetchFn }),
        raw
      );
      const finish = (status?: number) => {
        safely(
          () =>
            diagnostic?.({
              phase: "physical_end",
              name: captured.name,
              home: captured.home,
              captured: !!observation,
              status,
            }),
          undefined
        );
        safely(() => observation?.finish(status), undefined);
      };
      try {
        return Promise.resolve(execute(args, api, extraOptions)).then(
          (result) => {
            finish(safely(() => result.meta?.response?.status, undefined));
            return result;
          },
          (error) => {
            finish();
            throw error;
          }
        );
      } catch (error) {
        finish();
        throw error;
      }
    };
  }
  return { withTelemetry, physicalBaseQuery };
}
